import React, { useState, useRef, useEffect } from 'react';
import { Stack, Box, CircularProgress, useMediaQuery, useTheme } from '@mui/material';
import { SearchFilterSortBar } from 'components/pages/certificates/productPage/SearchFilterSortBar';
import { ScansSortDrawer } from 'components/pages/certificates/productPage/ScansSortDrawer';
import ScansFilterDrawer from 'components/pages/certificates/productPage/ScansFilterDrawer';
import SortMenuDesktop from 'components/pages/certificates/SortMenuDesktop';
import SortConfigPopover from 'components/pages/certificates/SortConfigPopover';
import { ViewToggle } from 'pages/CertificatePage/components/ViewToggle';
import { ScansList } from 'components/pages/certificates/productPage/ScansList';
import { MapHistory } from 'components/pages/certificates/productPage/MapHistory';
import { useScanLogs, useDeferredLoading } from 'hooks';
import { useFillHeight } from 'hooks/useFillHeight';
import { PAGE_HEADER_STICKY_HEIGHT } from 'components/UI/PageHeader';
import { PAGE_GUTTER_MOBILE } from 'constants/layout';
import { elevation } from 'settings/tokens';

/** Rows pulled per infinite-scroll step in the Scans list (VERA-476). */
const SCANS_PAGE_SIZE = 20;

/**
 * VERA-315 — SORT IS A MENU ON DESKTOP, A BOTTOM SHEET ON A PHONE.
 *
 * Both halves were the sheet, so at 1280 clicking Sort slid a phone drawer up from
 * the bottom of the window. The certificates list and the Activity tab had each
 * already solved this: branch on width, and at md+ anchor `SortMenuDesktop` (the
 * field picker, before anything is sorted) or `SortConfigPopover` (once a sort
 * exists) to the Sort button. This mirrors that branch with THIS screen's fields.
 *
 * A popover rather than the `DesktopSheetContext` dialog `DrawerPopup` now gives
 * every other sheet: this one has a clear 44px trigger sitting right there, and a
 * menu belongs next to the control that opened it. A centred dialog for a two-line
 * field list would be the same over-weight surface, just centred.
 *
 * Keys must match `ScansSortField`; labels match what `SortField` resolves those
 * keys to, so the picker and the configure popover beside it say the same word.
 */
const SCANS_SORT_FIELDS = ['owner_name', 'status', 'date', 'location'];

const SCANS_SORT_MENU_FIELDS = [
  { label: 'Owner name', value: 'owner_name' },
  { label: 'Status', value: 'status' },
  { label: 'Date', value: 'date' },
  { label: 'Location', value: 'location' },
];

interface ScansTabContentProps {
  certificateId: string;
  isShowScanHistory: boolean;
  /**
   * Reports map/list mode upward. The page needs it because the sticky footer is
   * justified by the LIST's infinite scroll — the map doesn't scroll, so pinning the
   * attribution there would just eat map area (VERA-476).
   */
  onViewModeChange?: (mode: 'map' | 'list') => void;
  /**
   * Let the MAP fill the viewport and bleed to both edges (VERA-315).
   *
   * Set by the Scans sub-view, which is a screen of its own. Left off wherever
   * this content is embedded in a scrolling page, where a viewport-tall map
   * would swallow the scroll.
   */
  fillViewport?: boolean;
  /**
   * Rendered directly ABOVE the map in fill mode (VERA-315).
   *
   * The Vera attribution. In fill mode the map runs to the bottom edge, so the
   * attribution cannot sit under it — and the shell cannot simply put it above
   * the whole content, because that lands it between the page title and the
   * search bar, floating in the header with nothing to belong to. Passing it in
   * as a slot puts it on the map's top edge, where it reads as the caption of
   * the surface below it.
   */
  attribution?: React.ReactNode;
  /**
   * VERA-315 — a scan to open selected, forwarded straight to `MapHistory` (see the
   * prop's note there for what happens when the scan has no coordinates).
   *
   * Deliberately NOT also forced into `list` mode or used to scroll the list: map is
   * this screen's default view precisely because "where has my item been?" is its
   * first question, and a deep-link that names one scan is asking exactly that.
   */
  focusScanId?: string | null;
}

/**
 * Bottom reserve for the filled map on a PHONE: NONE.
 *
 * It was 96, to keep the scan card the Map docks below itself on-screen. But the
 * card only exists once a pin is selected, so on every other view — including the
 * empty state — the reserve was 96px of white below a map that is supposed to
 * touch the bottom edge. Zero makes the common case correct; when a card does
 * dock, the page gains the card's height in scroll, which is what it did before
 * this screen filled the viewport at all.
 *
 * On DESKTOP the reserve is not a constant at all — it is the measured height of
 * the attribution, which now sits BELOW the map in flow rather than over it. See
 * `attributionHeight`.
 */
const MAP_BOTTOM_RESERVE = 0;

/**
 * Height of the desktop map's bottom fade, in px.
 *
 * BOTTOM ONLY, and that is a choice rather than a default. Content continues
 * below the map here — the attribution always, the docked scan card whenever a
 * pin is selected — and a bottom fade is what says "this surface hands off
 * downward". An all-edges fade is for an inset panel floating in a page, and it
 * would actively undo the other half of this layout: the left and right edges
 * were just brought back inside the content column so they line up with the
 * search bar above them, and dissolving them would blur the alignment we went
 * there to get.
 *
 * It is also the treatment the PHONE layout already uses — the attribution rides
 * on a `linear-gradient(to top, background.default …)` over the map's bottom — so
 * the two widths read as one idea rather than two.
 *
 * Rendered as an overlay rather than a `mask-image` on purpose: the docked scan
 * card is a child of the Map's own frame, so a mask on that frame would fade the
 * card's text along with the tiles. An overlay can be positioned against the map
 * REGION alone. It is `pointer-events: none`, so panning and zooming through it
 * are unaffected.
 */
const MAP_BOTTOM_FADE = 120;

/**
 * The Map's own frame border, top + bottom, in px.
 *
 * It has to be in the reserve because `mapHeight` sizes the map REGION while the
 * thing that must end at the viewport's bottom is the frame AROUND it plus the
 * attribution below. Left out, the page overflowed by exactly 2px — invisible on
 * its own, but the shell is `overflow: hidden` in map mode so it comes off the
 * attribution's bottom padding rather than being scrolled to.
 */
const MAP_FRAME_BORDER = 2;

export const ScansTabContent: React.FC<ScansTabContentProps> = ({
  certificateId,
  isShowScanHistory,
  onViewModeChange,
  fillViewport = false,
  attribution,
  focusScanId,
}) => {
  const theme = useTheme();
  /**
   * `md` and up the map is a contained CARD with the attribution below it; below
   * `md` it stays the full-bleed, edge-to-edge surface the phone has always had.
   *
   * A single boolean drives BOTH the CSS arrangement and the height arithmetic
   * rather than mixing sx breakpoints with a JS check. The two cannot then
   * disagree: if this ever evaluates stale, the layout is merely in the other
   * mode, not half in each.
   */
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true });

  const mapFillRef = useRef<HTMLDivElement | null>(null);

  /**
   * The attribution's real height, measured, because on desktop it is now in FLOW
   * below the map and the map has to stop short by exactly that much — the shell
   * is `height: 100dvh; overflow: hidden` in map mode, so anything the map does
   * not leave room for is not scrolled to, it is cut off.
   *
   * Measured rather than a constant for the reason `useFillHeight` gives at
   * length: the Footer's height depends on its padding AND on whether the brand
   * is white-labelled (which swaps the wordmark for a bare spacer), and a hand
   * kept number would be wrong for one of those two cases.
   */
  const attributionRef = useRef<HTMLDivElement | null>(null);
  const [attributionHeight, setAttributionHeight] = useState<number>(0);
  useEffect(() => {
    const el = attributionRef.current;
    if (!el) {
      setAttributionHeight(0);
      return;
    }
    const measure = () => setAttributionHeight(el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [attribution, isDesktop, fillViewport]);

  /**
   * The scan card the Map docks BELOW itself once a pin is selected, measured the
   * same way and for the same reason.
   *
   * It is a child of the Map's own frame, so it lands between the map and the
   * attribution and it only exists some of the time. Unreserved it pushed the
   * attribution clean off the bottom of a screen that cannot scroll — verified
   * with real scan rows, where selecting a pin put "POWERED BY VERA" below the
   * fold and cut the card itself in half. Reserving it means the map gives up
   * exactly the card's height while a pin is selected and takes it back on
   * deselect.
   *
   * Derived from the FRAME rather than by reaching into the Map's internals:
   * `frame - border - map` is the docked height by construction. It does not
   * feed back on itself — the card's height depends on its own content, not on
   * how tall the map is — so the one extra layout pass settles.
   */
  const mapFrameRef = useRef<HTMLDivElement | null>(null);
  const [dockedHeight, setDockedHeight] = useState<number>(0);

  const mapFillHeight = useFillHeight(
    mapFillRef,
    isDesktop
      ? attributionHeight + MAP_FRAME_BORDER + dockedHeight
      : MAP_BOTTOM_RESERVE,
  );
  /** The map REGION's height — what the Map is told to be, sans its own frame. */
  const mapPx = fillViewport && mapFillHeight ? mapFillHeight : 500;

  const [scansQuery, setScansQuery] = useState<string>('');
  // Map is the default (VERA-476): the Scans tab's first question is "where has my item
  // been?", and a map answers that at a glance where a list makes you read every row.
  // The list stays one tap away for the detail.
  const [scansViewMode, setScansViewMode] = useState<'map' | 'list'>('map');
  const [scansFilterOpen, setScansFilterOpen] = useState<boolean>(false);
  const [scansSortOpen, setScansSortOpen] = useState<boolean>(false);
  const [scansSortStep, setScansSortStep] = useState<'pick' | 'configure'>('pick');
  /** Desktop only: the Sort button, as the anchor for whichever popover opens. */
  const [scansSortMenuAnchor, setScansSortMenuAnchor] = useState<HTMLElement | null>(null);
  const [scansSortConfigAnchor, setScansSortConfigAnchor] = useState<HTMLElement | null>(null);
  const [scansSortField, setScansSortField] = useState<string | null>(null);
  const [scansSortDirection, setScansSortDirection] = useState<'ASC' | 'DESC' | null>(null);
  const [scansAdditionalSorts, setScansAdditionalSorts] = useState<
    Array<{ field: string; direction: 'ASC' | 'DESC' }>
  >([]);
  const [scansOwnerIds, setScansOwnerIds] = useState<string[]>([]);
  const [scansSelectedStatuses, setScansSelectedStatuses] = useState<string[]>([]);
  const [scansDateFrom, setScansDateFrom] = useState<string>('');
  const [scansDateTo, setScansDateTo] = useState<string>('');
  const [scansSelectedRange, setScansSelectedRange] = useState<
    'last_month' | 'last_3m' | 'last_6m' | 'last_12m' | null
  >(null);
  const [scansSelectedLocationIds, setScansSelectedLocationIds] = useState<string[]>([]);

  // VERA-476: the list previously took the hook's default limit of 20 and never
  // paginated, so a certificate with more scans silently showed only its 20 most
  // recent — contradicting the epic's "list shows all scans" criterion. We grow a
  // single window rather than accumulating pages client-side: the API returns the
  // top N by date for `limit: N, offset: 0`, so re-requesting with a larger N is
  // both simpler and immune to the drift that manual page-accumulation invites.
  const [visibleCount, setVisibleCount] = useState<number>(SCANS_PAGE_SIZE);

  const {
    data: scanLogsData,
    isLoading: isLoadingScans,
    isRefetching: isRefetchingScans,
  } = useScanLogs({
    certificateId,
    limit: visibleCount,
  });

  /*
   * `isLoadingScans` alone, NOT `|| isRefetchingScans` — see the call site below for
   * why that second term was actively wrong. Deferred for the same reason as every
   * other surface here: the skeleton is footprint-exact (`ScansListSkeleton` takes its
   * row height from the same `LIST_ROW_LEADING_SIZE` the real rows use), so what was
   * visible was the blink, not a shift.
   */
  const showScansSkeleton = useDeferredLoading(isLoadingScans);

  const totalScans = scanLogsData?.total ?? 0;
  const loadedScans = scanLogsData?.data?.length ?? 0;
  const hasMoreScans = loadedScans < totalScans;

  useEffect(() => {
    onViewModeChange?.(scansViewMode);
  }, [scansViewMode, onViewModeChange]);

  // See `dockedHeight`. Only on desktop, where the map is a contained card and
  // the attribution sits below it in flow — the phone's overlay attribution is
  // positioned against the outer box and is unaffected by what the frame holds.
  useEffect(() => {
    const el = mapFrameRef.current;
    if (!el || !isDesktop || !fillViewport || scansViewMode !== 'map') {
      setDockedHeight(0);
      return;
    }
    const measure = () =>
      setDockedHeight(
        Math.max(0, Math.round(el.getBoundingClientRect().height) - MAP_FRAME_BORDER - mapPx),
      );
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isDesktop, fillViewport, scansViewMode, mapPx]);

  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const sentinel = loadMoreRef.current;
    // Only observe in list mode — the map fetches its own single large page.
    if (!sentinel || !hasMoreScans || scansViewMode !== 'list') return;

    const observer = new IntersectionObserver(
      entries => {
        // Guard on isRefetchingScans: the sentinel stays mounted while the next page is
        // in flight, so without this the observer would queue several pages at once.
        if (entries[0]?.isIntersecting && !isRefetchingScans) {
          setVisibleCount(count => count + SCANS_PAGE_SIZE);
        }
      },
      { rootMargin: '200px' },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMoreScans, isRefetchingScans, scansViewMode]);

  // pb:1, not 4. The footer already brings its own 24px of top padding, so 32px here
  // stacked into a ~56px void between the scan card and "Powered by" in map view.
  // Kept non-zero so the last list row doesn't hug the pinned footer.
  //
  // ZERO in map view, where there is no last row to protect: the map is sized to
  // end at the viewport's bottom, so 8px below it is 8px the shell's `overflow:
  // hidden` clips — and on desktop it clipped the attribution's own bottom
  // padding instead of clipping nothing.
  return (
    <Stack sx={{ pb: scansViewMode === 'map' ? 0 : 1 }}>
      {/* Pins BELOW the page header, so header + toolbar read as one pinned
          block with the hairline at its bottom — the certificates page draws
          exactly that, from a single HeaderBar element. */}
      <SearchFilterSortBar
        subject='scans'
        stickyTop={PAGE_HEADER_STICKY_HEIGHT}
        query={scansQuery}
        onQueryChange={v => setScansQuery(v)}
        // Sort is meaningless against a cluster map — drop it in map mode so the
        // row stays search + filter + the view toggle.
        showSort={scansViewMode === 'list'}
        // Only the list scrolls — pinning the bar over a fixed-height map would take
        // 72px of map for nothing.
        sticky={scansViewMode === 'list'}
        viewToggle={
          isShowScanHistory ? (
            <ViewToggle value={scansViewMode} onChange={setScansViewMode} />
          ) : undefined
        }
        filterBadgeCount={
          scansOwnerIds.length +
          scansSelectedStatuses.length +
          (scansDateFrom ? 1 : 0) +
          (scansDateTo ? 1 : 0) +
          (scansSelectedRange ? 1 : 0) +
          scansSelectedLocationIds.length
        }
        onOpenFilter={() => setScansFilterOpen(true)}
        sortBadgeCount={
          scansSortField !== null && scansSortDirection !== null
            ? 1 + scansAdditionalSorts.length
            : 0
        }
        onOpenSort={e => {
          const hasSort = scansSortField !== null && scansSortDirection !== null;
          if (isDesktop) {
            // `e.currentTarget` is the Sort button itself, read synchronously —
            // the same anchor the certificates list hands its two popovers.
            const anchor = (e?.currentTarget as HTMLElement) ?? null;
            if (hasSort) setScansSortConfigAnchor(anchor);
            else setScansSortMenuAnchor(anchor);
            return;
          }
          setScansSortStep(hasSort ? 'configure' : 'pick');
          setScansSortOpen(true);
        }}
        showSortClear={!!(scansSortField !== null && scansSortDirection !== null)}
        onClearSort={() => {
          setScansSortField(null);
          setScansSortDirection(null);
          setScansAdditionalSorts([]);
        }}
      />

      {/* PHONE ONLY. Gated rather than merely left closed, matching how the
          certificates list and the Activity tab mount theirs, so the desktop tree
          carries no drawer that a stray state flip could open. */}
      {!isDesktop && (
      <ScansSortDrawer
        open={scansSortOpen}
        onClose={() => {
          setScansSortOpen(false);
          setScansSortStep('pick');
        }}
        step={scansSortStep}
        setStep={setScansSortStep}
        primaryField={(scansSortField as any) || 'date'}
        primaryDirection={scansSortDirection || 'ASC'}
        additionalSorts={scansAdditionalSorts as any}
        onPrimaryFieldChange={field => {
          setScansSortField(field);
          if (!scansSortDirection) setScansSortDirection('ASC');
        }}
        onPrimaryDirectionChange={setScansSortDirection as any}
        onClearAll={() => {
          setScansSortField(null);
          setScansSortDirection(null);
          setScansAdditionalSorts([]);
        }}
        /*
         * Nothing to do, and that is the point. This used to close the sheet and
         * rewind it to the `pick` step, which was right when it fired on a
         * deliberate Apply press. The sheet auto-applies now, so this runs 400ms
         * after any field or direction change — closing here would dismiss the
         * sheet the moment the user picked a field, and rewinding the step would
         * yank them out of `configure` mid-edit. The sort itself is already
         * committed by `onPrimaryFieldChange` / `onPrimaryDirectionChange` /
         * `onAdditionalChange` above. Dismissal belongs to `onClose`.
         */
        onApply={() => {}}
        /*
          ONE commit callback, fired on Apply. Previously three granular ones
          (change / remove / add) that each wrote straight to page state, so the
          list re-sorted underneath the still-open sheet.
        */
        onAdditionalChange={next => setScansAdditionalSorts(next as any)}
      />
      )}

      {/* DESKTOP: the field picker, `@repo/ui`'s `MenuButton` in controlled mode —
          literally the component `apps/platform`'s Sort renders, so row height,
          radius, hover and keyboard behaviour cannot drift from it. Opens only when
          nothing is sorted yet; once a sort exists the button opens the configure
          popover below instead. */}
      {isDesktop && (
        <SortMenuDesktop
          anchorEl={scansSortMenuAnchor}
          open={Boolean(scansSortMenuAnchor)}
          onClose={() => setScansSortMenuAnchor(null)}
          fields={SCANS_SORT_MENU_FIELDS}
          onPickField={(field, anchor) => {
            setScansSortField(field);
            if (!scansSortDirection) setScansSortDirection('ASC');
            setScansSortMenuAnchor(null);
            // Hand off to the configure popover — see `SortMenuDesktop`'s
            // "The pick HANDS OFF".
            setScansSortConfigAnchor(anchor);
          }}
        />
      )}

      {isDesktop && (
        <SortConfigPopover
          anchorEl={scansSortConfigAnchor}
          open={Boolean(scansSortConfigAnchor)}
          onClose={() => setScansSortConfigAnchor(null)}
          availableFields={SCANS_SORT_FIELDS}
          primaryField={scansSortField || 'date'}
          primaryDirection={scansSortDirection || 'ASC'}
          additionalSorts={scansAdditionalSorts}
          disabledFields={[
            ...(scansSortField ? [scansSortField] : []),
            ...scansAdditionalSorts.map(sort => sort.field),
          ]}
          onPrimaryFieldChange={field => {
            setScansSortField(field);
            if (!scansSortDirection) setScansSortDirection('ASC');
          }}
          onPrimaryDirectionChange={direction => setScansSortDirection(direction)}
          onClearAll={() => {
            setScansSortField(null);
            setScansSortDirection(null);
            setScansAdditionalSorts([]);
          }}
          onChangeAdditional={(index, next) =>
            setScansAdditionalSorts(current =>
              current.map((sort, i) => (i === index ? next : sort))
            )
          }
          onRemoveAdditional={index =>
            setScansAdditionalSorts(current => current.filter((_, i) => i !== index))
          }
          onAddAdditional={field =>
            setScansAdditionalSorts(current => [...current, { field, direction: 'DESC' }])
          }
        />
      )}

      <ScansFilterDrawer
        open={scansFilterOpen}
        onClose={() => setScansFilterOpen(false)}
        certificateId={certificateId}
        initialValues={{
          ownerIds: scansOwnerIds,
          statuses: scansSelectedStatuses,
          dateFrom: scansDateFrom,
          dateTo: scansDateTo,
          selectedRange: scansSelectedRange,
          locationIds: scansSelectedLocationIds,
        }}
        /* No `setScansFilterOpen(false)` — the drawer auto-applies, so this fires
           400ms after any field changes and closing here would dismiss the sheet
           on the user's first tap. Dismissal is `onClose`'s job. */
        onApply={values => {
          setScansOwnerIds(values.ownerIds || []);
          setScansSelectedStatuses(values.statuses || []);
          setScansDateFrom(values.dateFrom || '');
          setScansDateTo(values.dateTo || '');
          setScansSelectedRange(values.selectedRange || null);
          setScansSelectedLocationIds(values.locationIds || []);
        }}
      />

      {scansViewMode === 'list' &&
        isShowScanHistory &&
        (() => {
          const scansForList = (scanLogsData?.data || []).filter((s: any) => {
            if (scansOwnerIds.length === 0) return true;
            const email = (s.user?.email || '').toLowerCase();
            return scansOwnerIds.some(id => id.toLowerCase() === email);
          });
          return (
            <ScansList
              scans={scansForList}
              /* Deferred, and NARROWED to the first load. `isRefetchingScans` was in
               here too, so growing the window with "load more" — which changes the
               query key — replaced rows already on screen with a skeleton, which is
               the one thing §19 says a skeleton must never do. The sentinel below
               has its own spinner for that case. */
            isLoading={showScansSkeleton}
              query={scansQuery}
              selectedStatuses={scansSelectedStatuses}
              dateFrom={scansDateFrom}
              dateTo={scansDateTo}
              selectedRange={scansSelectedRange}
              selectedLocationIds={scansSelectedLocationIds}
              sortField={scansSortField as any}
              sortDirection={scansSortDirection as any}
              additionalSorts={scansAdditionalSorts as any}
            />
          );
        })()}

      {/* Infinite scroll: a sentinel below the list pulls the next page as it comes into
          view. This is a scan HISTORY — the reader is browsing, not hunting a specific
          row — so making them tap "Load more" every 20 rows is friction with no payoff.
          The spinner keeps the loading state honest, and `rootMargin` starts the fetch
          before the sentinel is actually visible so the list rarely stalls. */}
      {scansViewMode === 'list' && isShowScanHistory && hasMoreScans && (
        <Stack ref={loadMoreRef} alignItems='center' justifyContent='center' sx={{ py: 4 }}>
          <CircularProgress size={22} />
        </Stack>
      )}

      {scansViewMode === 'map' && isShowScanHistory && (() => {
        /* VERA-315: the map fills the screen. On a PHONE it also runs edge to
           edge — it was a 500px card inside a scrolling page, which is the worst
           of both — a pan/zoom surface competing with page scroll for the same
           gesture, on a screen whose one job is "where has this been?".

           At `md` and up it does NOT bleed. Measured at a 1280 viewport the map
           ran 80 → 1200 while the column it belongs to runs 104 → 1176, so it
           was 24px wider than every other block on the page on each side,
           including the search bar directly above it. Bleeding is a phone
           idiom — there is no column there to be outside of — and on a desktop
           page it just reads as a mis-sized element. So `mx` cancels the
           gutter at `xs` only, and the map takes 100% of its container at `md`,
           DERIVED rather than hardcoded so it stays correct if the column's
           width changes.

           The height is MEASURED from the element's own top rather than a
           `calc(100dvh - constant)`, because the chrome above it (page header,
           search bar, an optional chip row) changes height independently and a
           constant would need hand-syncing with all three. Same approach the
           platform's Table uses for `fillBottom`. */
        const isCard = fillViewport && isDesktop;

        return (
        <Box
          ref={mapFillRef}
          sx={{
            mx: fillViewport ? { xs: `-${PAGE_GUTTER_MOBILE}px`, md: 0 } : 0,
            position: 'relative',
          }}
        >
          {/* PHONE ONLY: the attribution sits OVER the map, not above it in flow.
              In flow it costs the map a band of height and reads as a caption
              for the surface below it; as an overlay the map genuinely runs to
              the bottom edge and the attribution rides on top of it.

              `pointerEvents: 'none'` on the wrapper so it never eats a pan or a
              pin tap — the link itself takes them back with `auto`, so the
              wordmark is still clickable. The gradient is what keeps it legible:
              map tiles are arbitrary, and a bare grey wordmark disappears over a
              city block or a body of water.

              On DESKTOP it moves below the card — see the bottom of this block.
              There is room for it there once the map is a contained card rather
              than a full-bleed surface, and stamped ON the map it read as part
              of the map's own chrome (Google's own attribution lives in exactly
              that spot) rather than as the page's footer. */}
          {fillViewport && attribution && !isDesktop && (
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 2,
                pointerEvents: 'none',
                '& a': { pointerEvents: 'auto' },
                background: theme =>
                  `linear-gradient(to top, ${theme.palette.background.default} 35%, transparent)`,
                pb: 'env(safe-area-inset-bottom, 0px)',
              }}
            >
              {attribution}
            </Box>
          )}

          {/* The CARD frame. The Map already draws the house control chrome — a
              1px `primary[100]` border at the shape token's radius, with
              `overflow: hidden` so Google's square-cornered `.gm-style` is
              actually CLIPPED rather than poking through the corners — and
              measured, that is character for character the Filter button beside
              it (10px, `#E4E4E7`). So this adds the one thing it was missing:
              the lift. `stateCardSurface` records why a card needs it here at
              all — the page surface is near-white, so a 1px border on its own
              reads flat.

              The radius is read from the SAME token the Map uses rather than a
              literal, so there is exactly ONE arc. A second, different radius on
              a wrapper with `overflow: hidden` is the classic version of this
              bug in reverse: the outer clip cuts the inner card's border at the
              corners. `theme.shape.borderRadius` is tenant-configurable (it
              measures 10, not the 12 in the token file), which is precisely why
              it must not be copied as a number. */}
          <Box
            ref={mapFrameRef}
            sx={{
              position: 'relative',
              ...(isCard && {
                borderRadius: theme => `${theme.shape.borderRadius}px`,
                boxShadow: elevation.card,
              }),
            }}
          >
            <MapHistory
              mapHeight={`${mapPx}px`}
              defaultZoom={4}
              certificateId={certificateId}
              focusScanId={focusScanId}
              // Same criteria the list gets, so search/filters apply in BOTH views.
              filters={{
                query: scansQuery,
                selectedStatuses: scansSelectedStatuses,
                selectedLocationIds: scansSelectedLocationIds,
                dateFrom: scansDateFrom,
                dateTo: scansDateTo,
                selectedRange: scansSelectedRange,
              }}
            />

            {/*
              NO BOTTOM FADE. It was built, measured working, and removed.

              It rendered correctly — 120px, correctly positioned at the map's
              bottom, `pointer-events: none` so it never ate a pan — and was
              still invisible, because it faded to the page background (white)
              over map tiles that are ALREADY near-white: pale land on pale
              water. A fade-to-background only reads when the content underneath
              is darker than the page, and this palette never is.

              Strengthening it was the alternative and was rejected: a gradient
              heavy enough to register against near-white tiles is heavy enough
              to look like a rendering artefact over the darker ones, and the map
              palette is not ours to pin down. Containment is already carried by
              the card's border and elevation, which are visible unconditionally.

              Do not re-add it without checking it against the actual tiles at the
              zoom the page opens at — "it's in the DOM and correctly positioned"
              was true the entire time it could not be seen.
            */}
          </Box>

          {/* DESKTOP: the attribution in normal flow, BELOW the card. The map's
              height already reserves exactly this element's height (see
              `attributionHeight`), so the two together are one viewport. */}
          {fillViewport && attribution && isDesktop && (
            <Box ref={attributionRef}>{attribution}</Box>
          )}
        </Box>
        );
      })()}
    </Stack>
  );
};
